import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supa } from "./supabase";
import { rpc } from "./rpc";
import { guardarCuentaRecordada } from "./cuentaRecordada";
import { VISTAS, type Vista } from "./nav";

export interface Perfil {
  id?: string;
  nombre?: string | null;
  apellido?: string | null;
  email?: string | null;
  rol: string;
  sucursal?: string | null;
  sucursal_id?: number | null;
  avatar_url?: string | null;
}

// roles que pueden ver más de una sucursal (y por tanto usan el selector)
export const ROLES_MULTI_SUCURSAL = ["super_admin", "directivo", "solo_lectura"];

export function nombreCompleto(p: Perfil): string {
  return (
    [p.nombre, p.apellido].filter((x) => x && !x.includes("@")).join(" ") ||
    p.nombre ||
    ""
  );
}

interface AuthCtx {
  session: Session | null;
  perfil: Perfil | null;
  modulos: Set<string>;
  cargando: boolean;
  errorPerfil: string | null;
  puedeVer: (v: Vista) => boolean;
  salir: () => Promise<void>;
  /** Vuelve a leer api_mi_perfil y actualiza el contexto (lo usa Mi perfil
   *  para que el nombre/foto del encabezado se refresquen al guardar).
   *  No lanza: si la relectura falla se queda el perfil anterior — el dato
   *  ya se guardó, un aviso de error aquí sería engañoso. */
  refrescarPerfil: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [modulos, setModulos] = useState<Set<string>>(new Set());
  const [cargando, setCargando] = useState(true);
  const [errorPerfil, setErrorPerfil] = useState<string | null>(null);

  useEffect(() => {
    supa.auth
      .getSession()
      .then(({ data }) => {
        setSession(data.session);
        if (!data.session) setCargando(false);
      })
      // Sin este catch, un fallo aquí deja `cargando` en true PARA SIEMPRE y la
      // pantalla se queda esperando sin decir nada (mismo hallazgo que PILOT,
      // App.jsx). Cayendo a "sin sesión" al menos aparece el login.
      .catch(() => {
        setSession(null);
        setCargando(false);
      });
    const { data: sub } = supa.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) {
        // Vaciar la caché en CUALQUIER cierre, no solo en el botón Salir.
        // salir() recarga la página y con eso se lleva todo, pero el otro
        // camino —la sesión que vence sola, que rpc.ts detecta y cierra— NO
        // recarga: se cae al login en la misma pestaña. Las llaves de las
        // consultas son [rpc, params] y no llevan el id de la persona, así que
        // sin esto el siguiente que entre en esa misma pestaña puede ver, del
        // caché, datos de sucursales que no le tocan.
        qc.clear();
        setPerfil(null);
        setModulos(new Set());
        setCargando(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  // Depende del ID del usuario, NO del objeto session: supabase emite varios
  // objetos distintos para la misma sesión (getSession + onAuthStateChange) y
  // encadenar cancelaciones dejaba la pantalla en "cargando" para siempre.
  const uid = session?.user?.id ?? null;
  // el correo es un string, no un objeto: sirve de dependencia sin reventar el
  // efecto en cada emisión de sesión (ver arriba)
  const correo = session?.user?.email ?? null;

  useEffect(() => {
    if (!uid) return;
    let vivo = true;
    (async () => {
      setCargando(true);
      setErrorPerfil(null);
      try {
        const p = await rpc<Perfil | null>("api_mi_perfil");
        if (!vivo) return;
        if (!p) {
          // mismo comportamiento que la app actual: sin perfil, no hay acceso
          setErrorPerfil(
            "Tu usuario no tiene perfil asignado. Pide acceso al administrador.",
          );
          await supa.auth.signOut();
          return;
        }
        setPerfil(p);
        // con el perfil en mano ya se puede recordar la cuenta para el próximo
        // login ("Hola de nuevo" con foto y nombre); antes solo se guardaba al
        // editar el perfil, así que quien nunca entró ahí no se recordaba nunca
        void guardarCuentaRecordada(p, correo ?? p.email);
        const mods = await rpc<string[] | null>("api_mis_modulos");
        if (!vivo) return;
        setModulos(new Set(mods ?? []));
      } catch (e) {
        if (vivo) {
          setErrorPerfil(
            e instanceof Error ? e.message : "No se pudo cargar tu perfil.",
          );
        }
      } finally {
        // sin guardia: la pantalla nunca debe quedarse cargando indefinidamente
        setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [uid, correo]);

  const puedeVer = useCallback(
    (v: Vista) =>
      modulos.has(v) || v === "perfil" || v === "proximamente" ||
      // Hoy (pantalla de entrada, 25-oct-2026) no tiene módulo propio: la ve quien tenga al
      // menos una pantalla del Radar; cada tarjeta se arma en la base solo con SUS módulos
      (v === "hoy" && [...modulos].some((m) => VISTAS.includes(m as Vista) && m !== "hoy")) ||
      // La oficina tiene tres llaves (gerente, CEDIS, Dirección): basta una para entrar;
      // qué ve cada quien adentro lo decide la base (oficina_nivel, migración 226).
      (v === "oficina" && (modulos.has("oficina_cedis") || modulos.has("oficina_direccion"))),
    [modulos],
  );

  const salir = useCallback(async () => {
    try {
      await supa.auth.signOut();
    } finally {
      // Arrancar DE CERO, como app.js (location.reload) y PILOT (href="/"): la
      // caché de TanStack Query no lleva el id de la persona en su llave, así
      // que sin recargar, en una computadora compartida de sucursal el
      // siguiente que entre alcanza a ver los datos del anterior.
      // `replace` y no `href`: así el botón "atrás" tampoco regresa al tablero.
      // Va en `finally` porque aunque el logout falle por red, auth-js ya borró
      // la sesión local — quedarse en el tablero sería lo peor de los dos mundos.
      window.location.replace(import.meta.env.BASE_URL);
    }
  }, []);

  const refrescarPerfil = useCallback(async () => {
    try {
      const p = await rpc<Perfil | null>("api_mi_perfil");
      if (p) setPerfil(p);
    } catch {
      // relectura best-effort: el encabezado se queda con el perfil anterior
    }
  }, []);

  const valor = useMemo(
    () => ({
      session, perfil, modulos, cargando, errorPerfil, puedeVer, salir,
      refrescarPerfil,
    }),
    [session, perfil, modulos, cargando, errorPerfil, puedeVer, salir, refrescarPerfil],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth fuera de <AuthProvider>");
  return c;
}
